import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Calendar, Award, CheckCircle, RotateCcw, Send } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { PageHeader, BackButton, AppButton, AppBadge, FileUpload, FilePreview } from "../../ui";
import CommonToaster from "../../Common/CommonToaster";
import "./general-assignments.scss";

const StudentAssignmentView = () => {
  const { id } = useParams();
  const [assignment, setAssignment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [textResponse, setTextResponse] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const fetchData = useCallback(() => {
    setLoading(true);
    apiClient.get(`/general-assignments/student/detail/${id}`)
      .then(({ data }) => {
        setAssignment(data.assignment);
        setSubmission(data.submission);
        if (data.submission?.textResponse) setTextResponse(data.submission.textResponse);
      })
      .catch(() => setToast({ show: true, message: "Failed to load assignment", type: "error" }))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!textResponse.trim() && !files.length) {
      setToast({ show: true, message: "Please provide a response or upload files", type: "error" });
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("textResponse", textResponse.trim());
      files.forEach((f) => formData.append("attachments", f));

      await apiClient.post(`/general-assignments/${id}/submit`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setToast({ show: true, message: "Assignment submitted successfully!", type: "success" });
      setFiles([]);
      fetchData();
    } catch (err) {
      setToast({
        show: true,
        message: err.response?.data?.message || "Failed to submit",
        type: "error",
      });
    }
    setSubmitting(false);
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

  if (loading) return <div className="ga-page ga-loading">Loading...</div>;
  if (!assignment) return <div className="ga-page"><BackButton />Assignment not found</div>;

  const isOverdue = new Date(assignment.dueDate) < new Date();
  const canSubmit = !submission || submission.status === "resubmit_requested";
  const showForm = canSubmit && assignment.status !== "closed" && !isOverdue;

  return (
    <div className="ga-page">
      <PageHeader
        backButton
        title={assignment.title}
        description={assignment.batchId?.name || ""}
      />

      <div className="ga-student-view">
        {/* Meta info */}
        <div className="ga-detail__meta">
          <span className="ga-detail__meta-item">
            <Calendar size={15} /> Due: {formatDate(assignment.dueDate)}
          </span>
          <span className="ga-detail__meta-item">
            <Award size={15} /> Max: {assignment.maxMarks} marks
          </span>
          <AppBadge variant="outline" color={isOverdue ? "rose" : "emerald"}>
            {isOverdue ? "Overdue" : "Open"}
          </AppBadge>
        </div>

        {/* Description */}
        {assignment.description && (
          <div className="ga-student-view__section">
            <div className="ga-student-view__section-title">Instructions</div>
            <div className="ga-student-view__description">{assignment.description}</div>
          </div>
        )}

        {/* Assignment attachments */}
        {assignment.attachments?.length > 0 && (
          <div className="ga-student-view__section">
            <div className="ga-student-view__section-title">Reference Files</div>
            <FilePreview attachments={assignment.attachments} />
          </div>
        )}

        {/* Feedback display (if graded or resubmit requested) */}
        {submission?.status === "graded" && (
          <div className="ga-student-view__section">
            <div className={`ga-student-view__feedback ga-student-view__feedback--graded`}>
              <span className="ga-student-view__feedback-label">
                <CheckCircle size={14} /> Graded — {submission.marks}/{assignment.maxMarks}
              </span>
              {submission.feedback && (
                <span className="ga-student-view__feedback-text">{submission.feedback}</span>
              )}
            </div>
          </div>
        )}

        {submission?.status === "resubmit_requested" && (
          <div className="ga-student-view__section">
            <div className={`ga-student-view__feedback ga-student-view__feedback--resubmit`}>
              <span className="ga-student-view__feedback-label">
                <RotateCcw size={14} /> Resubmission Requested
              </span>
              {submission.feedback && (
                <span className="ga-student-view__feedback-text">{submission.feedback}</span>
              )}
            </div>
          </div>
        )}

        {/* Previous submission display */}
        {submission?.status === "submitted" && (
          <div className="ga-student-view__section">
            <div className="ga-student-view__section-title">Your Submission</div>
            {submission.textResponse && (
              <div className="ga-review__text-response">{submission.textResponse}</div>
            )}
            {submission.attachments?.length > 0 && (
              <FilePreview attachments={submission.attachments} compact />
            )}
            <AppBadge variant="outline" color="blue" style={{ marginTop: "0.5rem" }}>
              Submitted — Awaiting Review
            </AppBadge>
          </div>
        )}

        {/* Submit form */}
        {showForm && (
          <div className="ga-student-view__section">
            <div className="ga-student-view__section-title">
              {submission?.status === "resubmit_requested" ? "Resubmit Your Work" : "Submit Your Work"}
            </div>
            <form className="ga-student-view__submit-form" onSubmit={handleSubmit}>
              <div className="ga-form__group" style={{ marginBottom: 0 }}>
                <label className="ga-form__label">Response (text, link, etc.)</label>
                <textarea
                  className="ga-form__textarea"
                  value={textResponse}
                  onChange={(e) => setTextResponse(e.target.value)}
                  placeholder="Write your answer, paste a GitHub link, or describe your work..."
                  rows={4}
                />
              </div>

              <FileUpload
                label="Upload Files"
                files={files}
                onChange={setFiles}
                maxFiles={5}
                hint="Upload your work — PDF, DOC, TXT, or images (JPG/PNG)"
              />

              <AppButton type="submit" disabled={submitting}>
                <Send size={16} />
                {submitting ? "Submitting..." : submission ? "Resubmit" : "Submit Assignment"}
              </AppButton>
            </form>
          </div>
        )}

        {/* Closed / Overdue + no submission */}
        {!showForm && !submission && (
          <div className="ga-student-view__section">
            <AppBadge variant="outline" color="rose">
              {assignment.status === "closed" ? "Assignment Closed" : "Deadline Passed"} — Submissions not accepted
            </AppBadge>
          </div>
        )}
      </div>

      <CommonToaster toast={toast} setToast={setToast} />
    </div>
  );
};

export default StudentAssignmentView;
